import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Table,
  Button,
  Select,
  Dialog,
  TableRow,
  MenuItem,
  TextField,
  TableCell,
  TableBody,
  InputLabel,
  FormControl,
  DialogActions,
  TableContainer,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";
import thaAxios from "../../../utils/thaAxiosService";
import PopularityRank from "../popularity-rank";

const AddPopularProductForm = () => {
  const [simData, setSimData] = useState<any[]>([]);
  const [countryListN, setCountryListN] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [country, setCountry] = useState<any>("0");
  const [product, setProduct] = useState<any>("0");
  const [subCategoryName, setSubCategoryName] = useState<string>("0");
  const [currency, setCurrency] = useState<any>("0");
  const [isOperatorLoading, setIsOperatorLoading] = useState<boolean>(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [allOperators, setAllOperators] = useState<any[]>([]);
  const [operator, setOperator] = useState<any>(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [perform, setPerform] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [origination, setOrigination] = useState<any>("0");
  const [displayOrder, setDisplayOrder] = useState<string>("");

  const [paginationModel, setPaginationModel] = useState<any>({
    page: 1,
    pageSize: 50,
  });
  const isFirstRender = useRef(true);

  const loadCountries = async () => {
    await fetch("/data/thacountry.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json: any) => {
        setCountryListN(json.thacountries);
      });
  };

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    setOperator(0);
    setRowData([]);
    setAllOperators([]);

    if (country !== "0" && country !== undefined) {
      getOperator();
    }
  }, [country]);

  useEffect(() => {
    setOperator(0);
    setRowData(filterOperatorsByProduct(allOperators));
  }, [product, allOperators]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    getSimData();
  }, [paginationModel]);

  const getProductData = () => {
    setPaginationModel((prev: any) => ({
      ...prev,
      page: 1,
    }));
  };

  const getApiErrorMessage = (error: any) => {
    const errors = error?.response?.data?.errors;

    if (errors) {
      const errorMessages = Object.values(errors).flat();

      if (errorMessages.length > 0) {
        return errorMessages.join(" ");
      }
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.title ||
      error?.message ||
      "Something went wrong"
    );
  };

  const getOperatorShortCode = (rowData: any) => {
    return (
      rowData?.operatorShortCode ||
      rowData?.OperatorShortCode ||
      rowData?.productOperator?.operatorShortCode ||
      ""
    );
  };

  const sortOperators = (operators: any[]) => {
    return operators.sort((a, b) =>
      a.operator?.operatorName.localeCompare(b.operator?.operatorName),
    );
  };

  const filterOperatorsByProduct = (operators: any[]) => {
    if (product === "0") {
      return operators;
    }

    return operators.filter(
      (item: any) => item.category?.categoryShortCode === product,
    );
  };

  const getOperator = async () => {
    setIsOperatorLoading(true);
    toast.dismiss();
    await axiosInterceptorInstance
      .post(`/FusionHubCrm/GetOperators`, {
        page: 1,
        recordsPerPage: 10000,
        operatorFilters: {
          countryIsoCode: country.isoCode2,
        },
      })
      .then((response: any) => {
        const responseData = response?.data.payload || [];
        const operators = responseData.map((item: any) => ({
          operator: item.operator,
          country: item.country,
          category: item.category,
        }));
        const sortedOperators = sortOperators(operators);
        const filteredOperators = filterOperatorsByProduct(sortedOperators);
        setAllOperators(sortedOperators);
        setRowData(filteredOperators);
        if (filteredOperators.length === 0) {
          toast.info("No operator found");
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsOperatorLoading(false);
      });
  };

  const getSimData = async () => {
    setIsLoading(true);
    setSimData([]);

    await axiosInterceptorInstance
      .post(`/FusionHubCrm/GetProducts`, {
        page: paginationModel.page,
        recordsPerPage: paginationModel.pageSize,
        filters: {
          isActive: true,
          ...(country !== "0" &&
            country !== undefined && { countryIsoCode: country.isoCode2 }),
          ...(operator?.operator?.operatorShortCode
            ? {
                operatorShortCode: operator.operator.operatorShortCode,
              }
            : {}),
          ...(product !== "0" && { categoryShortCode: product }),
          ...(product === "mobile" &&
            subCategoryName !== "0" && {
              subCategoryShortCode: subCategoryName,
            }),
          ...(currency !== "0" && { CurrencyCode: currency }),
        },
      })
      .then((response: any) => {
        const responseData = response.data.payload;
        const totalRecords = response.data.paginationInfo.totalCount;
        if (responseData.length === 0 && totalRecords > 0) {
          setPaginationModel((prev: any) => ({
            ...prev,
            page: paginationModel.page - 1,
          }));
        }
        if (totalRecords === 0) {
          toast.info("No product found");
        }
        setSimData(responseData);
        setTotalRows(totalRecords);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const upsertPopularProduct = async () => {
    toast.dismiss();
    if (origination === "0" || origination === undefined) {
      toast.error("Please select the Origination Country");
      return;
    }
    if (displayOrder === "") {
      toast.error("DisplayOrder is required");
      return;
    }

    setPerform(true);

    await thaAxios
      .post("CRMProductDisplayOrderRules/UpsertProductDisplayOrderRules", {
        name: selectedRowData.productName,
        productType: getSelectedProductType(selectedRowData),
        origination: origination.isoCode2,
        destination: selectedRowData.destination,
        OperatorShortCode: getOperatorShortCode(selectedRowData),
        productId: selectedRowData.productId.toString(),
        isActive: true,
        displayOrder: Number(displayOrder),
      })
      .then((res: any) => {
        const data = res.data;
        toast.success(data.message || "Popular product added successfully");
        setOpenDialog(false);
        setOrigination("0");
        setDisplayOrder("");
      })
      .catch((error: any) => {
        toast.error(getApiErrorMessage(error));
        console.error(error);
      })
      .finally(() => {
        setPerform(false);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "productId",
      headerName: "Product ID",
      width: 250,
    },
    {
      field: "productName",
      headerName: "Product Name",
      width: 280,
    },
    { field: "productType", headerName: "Type", width: 130 },
    { field: "categoryShortCode", headerName: "Category", width: 130 },
    { field: "subCategoryName", headerName: "Sub Category", width: 130 },
    { field: "vendorName", headerName: "Vendor", width: 100 },
    {
      field: "operatorShortCode",
      headerName: "Operator Short Code",
      width: 180,
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 180,
      renderCell: (params) => params.row.destinationName || params.value,
    },
  ];

  const getSelectedProductType = (productData: any) => {
    return productData?.categoryShortCode === "gift_cards" ? 6 : 3;
  };

  const handleDisplayOrderChange = (value: string) => {
    if (value === "") {
      setDisplayOrder("");
      return;
    }

    const displayOrderNumber = Number(value);

    if (Number.isNaN(displayOrderNumber)) return;

    if (displayOrderNumber < 0) {
      setDisplayOrder("0");
      return;
    }

    setDisplayOrder(value);
  };

  const getProductDestinationIsoCode = (row: any) => {
    return row.product?.productCountry?.countryIsoCode2 || "";
  };

  const getProductDestinationName = (row: any) => {
    const destinationIsoCode = getProductDestinationIsoCode(row);

    return (
      row.product?.productCountry?.countryName ||
      getCountryNameByIsoCode(destinationIsoCode)
    );
  };

  const onRowClicked = (params: GridRowParams) => {
    setSelectedRowData(params.row);
    setOrigination("0");
    setDisplayOrder("");
    setOpenDialog(true);
  };
  const getCountryNameByIsoCode = (isoCode: string) => {
    return (
      countryListN.find(
        (item) => item.isoCode2?.toLowerCase() === isoCode?.toLowerCase(),
      )?.countryName || isoCode
    );
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Add Popular Product</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <fieldset>
                <legend>General Information:</legend>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel
                        id="currency-select-label"
                        style={{ background: "#fff", padding: " 0 5px" }}
                      >
                        Currency
                      </InputLabel>
                      <Select
                        labelId="currency-select-label"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        <MenuItem value="0">Choose Currency</MenuItem>
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    {countryListN && (
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel
                          id="country-select-label"
                          style={{ background: "#fff", padding: " 0 5px" }}
                        >
                          Destination
                        </InputLabel>
                        <Select
                          labelId="country-select-label"
                          className="formControl"
                          autoWidth
                          value={country?.isoCode2 || 0}
                          defaultValue={country?.isoCode2 || 0}
                          onChange={(e: any) => {
                            const country = countryListN?.find(
                              (item) => item.isoCode2 === e.target.value,
                            );
                            setCountry(country);
                          }}
                        >
                          <MenuItem value="0">Select Destination</MenuItem>
                          {countryListN.map((item, index) => (
                            <MenuItem key={index} value={item?.isoCode2}>
                              {item?.countryName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        id="product-select-label"
                        style={{ background: "#fff", padding: " 0 5px" }}
                      >
                        Product Type
                      </InputLabel>
                      <Select
                        labelId="product-select-label"
                        className="formControl"
                        autoWidth
                        value={product}
                        defaultValue={product}
                        onChange={(e: any) => {
                          setProduct(e.target.value);
                          if (e.target.value !== "mobile") {
                            setSubCategoryName("0");
                          }
                        }}
                      >
                        <MenuItem value="0">Choose Type</MenuItem>
                        <MenuItem value="gift_cards">Gift Card</MenuItem>
                        <MenuItem value="mobile">Mobile</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {product === "mobile" && (
                    <Grid item xs={4}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel
                          id="subcategory-select-label"
                          style={{ background: "#fff", padding: " 0 5px" }}
                        >
                          Subcategory
                        </InputLabel>
                        <Select
                          labelId="subcategory-select-label"
                          className="formControl"
                          autoWidth
                          value={subCategoryName}
                          onChange={(e: any) =>
                            setSubCategoryName(e.target.value)
                          }
                        >
                          <MenuItem value="0">Choose Subcategory</MenuItem>
                          <MenuItem value="airtime">Airtime</MenuItem>
                          <MenuItem value="bundle">Bundle</MenuItem>
                          <MenuItem value="data">Data</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={4}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        id="operator-select-label"
                        style={{ background: "#fff", padding: " 0 5px" }}
                      >
                        Operator
                      </InputLabel>

                      <Select
                        labelId="operator-select-label"
                        className="formControl"
                        autoWidth
                        disabled={
                          isOperatorLoading ||
                          country === "0" ||
                          country === undefined
                        }
                        value={operator?.operator?.operatorId || "0"}
                        defaultValue={operator?.operator?.operatorId || "0"}
                        onChange={(e: any) => {
                          const operator = rowData?.find(
                            (item: any) =>
                              item.operator?.operatorId === e.target.value,
                          );

                          setOperator(operator);
                        }}
                      >
                        <MenuItem value="0">Choose Type</MenuItem>

                        {isOperatorLoading ? (
                          <div
                            className="circular-loader"
                            style={{ marginLeft: "0" }}
                          >
                            <CircularProgress size={40} />
                          </div>
                        ) : rowData.length > 0 ? (
                          rowData.map((item: any, index: any) => (
                            <MenuItem
                              key={index}
                              value={item.operator?.operatorId}
                            >
                              {item.operator?.operatorName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="0">No Operator</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid>
                  <Button
                    variant="contained"
                    onClick={getProductData}
                    disabled={isOperatorLoading}
                    sx={{
                      margin: "0 auto",
                      display: "block",
                      width: "280px !important",
                    }}
                  >
                    {isOperatorLoading ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
              </fieldset>
            </Grid>
          </Grid>

          {isLoading && (
            <div className="circular-loader" style={{ marginLeft: "0px" }}>
              <CircularProgress size={40} />
            </div>
          )}

          {simData?.length > 0 && (
            <div style={{ height: 550, width: "100%", marginTop: 16 }}>
              <DataGrid
                rows={
                  simData?.map((row, index) => ({
                    ...(row.product || {}),
                    id: `${paginationModel.page}-${row.product?.productId}-${index}`,
                    destination: getProductDestinationIsoCode(row),
                    destinationName: getProductDestinationName(row),
                    operatorShortCode:
                      row.product?.productOperator?.operatorShortCode || "",
                  })) || []
                }
                columns={columns}
                pagination
                rowCount={totalRows}
                paginationMode="server"
                pageSize={paginationModel.pageSize}
                rowsPerPageOptions={[10, 25, 50]}
                onPageSizeChange={(newPageSize) =>
                  setPaginationModel((prev: any) => ({
                    ...prev,
                    pageSize: newPageSize,
                  }))
                }
                page={paginationModel.page - 1}
                onPageChange={(newPage) =>
                  setPaginationModel((prev: any) => ({
                    ...prev,
                    page: newPage + 1,
                  }))
                }
                onRowClick={onRowClicked}
              />
            </div>
          )}

          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <Box style={{ padding: "0 16px " }}>
              <h1 className="heading" style={{ marginTop: "20px" }}>
                Add Popular Product
              </h1>
              {selectedRowData && (
                <TableContainer
                  component={Paper}
                  style={{
                    marginTop: "20px",
                    marginBottom: "20px",
                    padding: "0px",
                  }}
                >
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Product ID</strong>
                        </TableCell>
                        <TableCell>{selectedRowData.productId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Name</strong>
                        </TableCell>
                        <TableCell>{selectedRowData.productName}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Destination</strong>
                        </TableCell>
                        <TableCell>
                          {selectedRowData.destinationName ||
                            getCountryNameByIsoCode(
                              selectedRowData.destination,
                            )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Operator Short Code</strong>
                        </TableCell>
                        <TableCell>
                          {getOperatorShortCode(selectedRowData)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Product Type</strong>
                        </TableCell>
                        <TableCell>
                          {getSelectedProductType(selectedRowData)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <FormControl style={{ width: "100%", marginTop: "20px" }}>
                <InputLabel
                  id="origination-select-label"
                  style={{ background: "#fff", padding: " 0 5px" }}
                >
                  Origination
                </InputLabel>
                <Select
                  labelId="origination-select-label"
                  className="formControl"
                  autoWidth
                  value={origination?.isoCode2 || 0}
                  defaultValue={origination?.isoCode2 || 0}
                  onChange={(e: any) => {
                    const country = countryListN?.find(
                      (item) => item.isoCode2 === e.target.value,
                    );
                    setOrigination(country || "0");
                  }}
                >
                  <MenuItem value="0">Select Origination</MenuItem>
                  {countryListN.map((item, index) => (
                    <MenuItem key={index} value={item?.isoCode2}>
                      {item?.countryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                className="formControl"
                style={{ marginTop: "10px" }}
                label="DisplayOrder"
                variant="outlined"
                type="number"
                value={displayOrder}
                inputProps={{ min: 0 }}
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => handleDisplayOrderChange(e.target.value)}
              />

              <DialogActions>
                <Button
                  variant="contained"
                  className="dButton dButton2 dbuttonSec"
                  onClick={() => {
                    setOpenDialog(false);
                    setDisplayOrder("");
                  }}
                >
                  No
                </Button>
                <div style={{ width: "30px" }}></div>
                {!perform && (
                  <Button
                    variant="contained"
                    className="dButton dButton2"
                    onClick={() => {
                      upsertPopularProduct();
                    }}
                    autoFocus
                  >
                    Yes
                  </Button>
                )}
                {perform && (
                  <Button className="dButton dButton2" variant="contained">
                    <CircularProgress color="inherit" size={30} />
                  </Button>
                )}
              </DialogActions>
            </Box>
          </Dialog>
        </div>
      </div>
    </motion.div>
  );
};

const PopularProducts = () => {
  const location = useLocation();
  const isAddPopularProductPath = location.pathname.includes(
    "add-popular-product",
  );
  const [activeTab, setActiveTab] = useState(isAddPopularProductPath ? 1 : 0);

  useEffect(() => {
    setActiveTab(isAddPopularProductPath ? 1 : 0);
  }, [isAddPopularProductPath]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Popular Products</h2>
        <div className="boxInner">
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Popularity Rank" />
            <Tab label="Add Popular Product" />
          </Tabs>
        </div>
      </div>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
          <PopularityRank />
        </Box>
        <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
          <AddPopularProductForm />
        </Box>
      </Box>
    </motion.div>
  );
};

export default PopularProducts;
